import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2, Search, Users } from "lucide-react";
import { api, apiError, fmt } from "@/lib/api";

const STATUS_CLS = {
  active: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
  inactive: "border-white/15 bg-white/5 text-neutral-400",
  blocked: "border-red-500/40 bg-red-500/10 text-red-400",
};

export const CrmStatusPill = ({ status, testid }) => (
  <span data-testid={testid}
    className={`inline-block rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${STATUS_CLS[status] || STATUS_CLS.active}`}>
    {status || "active"}
  </span>
);

const FILTERS = [
  { id: "", label: "All" }, { id: "active", label: "Active" },
  { id: "inactive", label: "Inactive" }, { id: "blocked", label: "Blocked" },
];

export default function AdminCustomers() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [credit, setCredit] = useState(false);
  const [outstanding, setOutstanding] = useState(false);
  const [page, setPage] = useState(1);
  const debounce = useRef(null);

  const load = useCallback((pg = page, query = q, st = status, cr = credit, out = outstanding) => {
    const params = new URLSearchParams({ page: String(pg), page_size: "20" });
    if (query.trim()) params.set("q", query.trim());
    if (st) params.set("status", st);
    if (cr) params.set("credit", "true");
    if (out) params.set("outstanding", "true");
    api.get(`/admin/crm/customers?${params}`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e, "Couldn't load customers")));
  }, [page, q, status, credit, outstanding]);
  useEffect(() => { load(); }, [status, credit, outstanding, page]); // eslint-disable-line

  const onSearch = (v) => {
    setQ(v);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => { setPage(1); load(1, v); }, 350);
  };

  const toggle = (setter, key) => (e) => { setPage(1); setter(e.target.checked); };

  return (
    <div data-testid="admin-customers" className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-white">
          <Users className="h-6 w-6 text-amber-400" /> Customers
        </h1>
        <p className="mt-1 text-sm text-neutral-500">B2B customer directory — profiles, sales metrics, credit and receivables.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-600" />
          <input value={q} onChange={(e) => onSearch(e.target.value)} data-testid="cust-search"
            placeholder="Search name, business, email, phone…"
            className="h-10 w-72 max-w-full rounded-full border border-white/10 bg-black/40 pl-9 pr-3 text-sm text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none" />
        </div>
        {FILTERS.map((f) => (
          <button key={f.id} onClick={() => { setPage(1); setStatus(f.id); }} data-testid={`cust-filter-${f.id || "all"}`}
            className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${status === f.id ? "bg-amber-500 text-black" : "border border-white/10 text-neutral-400 hover:text-white"}`}>
            {f.label}
          </button>
        ))}
        <label className="flex cursor-pointer items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs text-neutral-400">
          <input type="checkbox" checked={credit} onChange={toggle(setCredit)} data-testid="cust-filter-credit" className="accent-amber-500" />
          Credit customers
        </label>
        <label className="flex cursor-pointer items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs text-neutral-400">
          <input type="checkbox" checked={outstanding} onChange={toggle(setOutstanding)} data-testid="cust-filter-outstanding" className="accent-amber-500" />
          With outstanding
        </label>
      </div>

      {!data ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-neutral-600" /></div>
      ) : data.customers.length === 0 ? (
        <div className="rounded-2xl border border-white/[0.07] bg-white/[0.02] py-16 text-center text-sm text-neutral-500" data-testid="cust-empty">
          No customers match these filters.
        </div>
      ) : (
        <>
          <div className="hidden overflow-x-auto rounded-2xl border border-white/[0.07] md:block">
            <table className="w-full text-left text-sm" data-testid="cust-table">
              <thead>
                <tr className="border-b border-white/[0.07] bg-white/[0.03] text-[11px] uppercase tracking-wider text-neutral-500">
                  <th className="px-3 py-3">Customer</th><th className="px-3 py-3">Contact</th>
                  <th className="px-3 py-3">Status</th><th className="px-3 py-3 text-right">Orders</th>
                  <th className="px-3 py-3 text-right">Lifetime sales</th><th className="px-3 py-3 text-right">Outstanding</th>
                  <th className="px-3 py-3">Credit</th>
                </tr>
              </thead>
              <tbody>
                {data.customers.map((c) => (
                  <tr key={c.id} onClick={() => navigate(`/admin/customers/${c.id}`)}
                    className="cursor-pointer border-b border-white/[0.05] hover:bg-white/[0.02]" data-testid={`cust-row-${c.id}`}>
                    <td className="max-w-[220px] px-3 py-3">
                      <p className="truncate font-bold text-white">{c.business_name || c.name}</p>
                      {c.business_name && <p className="truncate text-xs text-neutral-500">{c.name}</p>}
                    </td>
                    <td className="max-w-[200px] px-3 py-3">
                      <p className="truncate text-xs text-neutral-300">{c.email}</p>
                      <p className="text-xs text-neutral-600">{c.phone || c.contact_person || ""}</p>
                    </td>
                    <td className="px-3 py-3"><CrmStatusPill status={c.status} testid={`cust-status-${c.id}`} /></td>
                    <td className="px-3 py-3 text-right text-neutral-300">{c.total_orders}</td>
                    <td className="px-3 py-3 text-right font-bold text-white">{fmt(c.lifetime_sales)}</td>
                    <td className={`px-3 py-3 text-right font-bold ${c.outstanding > 0 ? "text-amber-300" : "text-neutral-600"}`}
                      data-testid={`cust-outstanding-${c.id}`}>{fmt(c.outstanding)}</td>
                    <td className="px-3 py-3">
                      {c.is_credit && (
                        <span className="rounded-full border border-sky-500/30 bg-sky-500/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-sky-300"
                          data-testid={`cust-credit-${c.id}`}>{c.credit_status}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 md:hidden" data-testid="cust-cards">
            {data.customers.map((c) => (
              <button key={c.id} onClick={() => navigate(`/admin/customers/${c.id}`)}
                className="w-full rounded-2xl border border-white/[0.07] bg-white/[0.02] p-4 text-left" data-testid={`cust-card-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-white">{c.business_name || c.name}</p>
                    <p className="truncate text-xs text-neutral-500">{c.email}</p>
                  </div>
                  <CrmStatusPill status={c.status} testid={`cust-card-status-${c.id}`} />
                </div>
                <p className="mt-2 text-xs text-neutral-500">
                  {c.total_orders} orders · lifetime {fmt(c.lifetime_sales)}
                  {c.outstanding > 0 && <span className="font-bold text-amber-300"> · due {fmt(c.outstanding)}</span>}
                  {c.is_credit && <span className="text-sky-300"> · credit</span>}
                </p>
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between" data-testid="cust-pagination">
            <p className="text-xs text-neutral-500">{data.total} customer(s) · page {data.page} of {data.pages}</p>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="cust-prev"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:text-white disabled:opacity-30">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="cust-next"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:text-white disabled:opacity-30">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
